import { useMutation, useQueryClient } from '@tanstack/react-query'
import { ChevronRight, Link2, Mail } from 'lucide-react'
import { useState } from 'react'
import { FormError } from '@/components/FormError'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { api } from '@/lib/api'
import type { Family, Invite } from '@/lib/families'
import { inviteState } from '@/lib/invites'
import { useIsPhone } from '@/lib/platform'

// The circle's invites that haven't been taken up yet, and the ones whose link ran out lately, each with a way to send
// it again or call it off. A short list on the page on a tablet or computer; on a phone one row that opens it.
export function WaitingToJoin({ family, canInvite }: { family: Family; canInvite: boolean }) {
  const phone = useIsPhone()
  const [open, setOpen] = useState(false)
  const count = family.invites.length
  if (count === 0) return null
  const title = 'Waiting to join'
  const list = <InviteList family={family} canInvite={canInvite} />
  if (!phone)
    return (
      <section aria-labelledby="waiting" className="overflow-hidden rounded-[1.25rem] border">
        <h2 id="waiting" className="flex items-baseline gap-2 bg-secondary px-4 py-3 text-base font-semibold">
          {title}
          <span className="text-sub font-normal text-muted-foreground">{count}</span>
        </h2>
        {list}
      </section>
    )
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="flex min-h-12 items-center justify-between gap-3 rounded-2xl bg-secondary px-4 text-left outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
      >
        <span className="font-medium">
          {count} {count === 1 ? 'invite' : 'invites'} waiting to join
        </span>
        <ChevronRight className="size-4 text-muted-foreground" aria-hidden />
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90svh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
            <DialogDescription>Sending one again gives them a fresh link for 7 days.</DialogDescription>
          </DialogHeader>
          {list}
        </DialogContent>
      </Dialog>
    </>
  )
}

// Email rows are keyed by email (or the name, for someone picked from the people you know): sending again replaces
// the invite, and its row should stay put to say so.
function InviteList({ family, canInvite }: { family: Family; canInvite: boolean }) {
  return (
    <ul className="flex flex-col">
      {family.invites.map((invite) => (
        <InviteRow
          key={invite.email ?? (invite.via === 'email' ? `name:${invite.name}` : invite.id)}
          familyId={family.id}
          invite={invite}
          canInvite={canInvite}
        />
      ))}
    </ul>
  )
}

function InviteRow({ familyId, invite, canInvite }: { familyId: string; invite: Invite; canInvite: boolean }) {
  const queryClient = useQueryClient()
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['family', familyId] })
  const [sent, setSent] = useState(false)
  const resend = useMutation({
    mutationFn: () => api(`/families/${familyId}/invites/${invite.id}/resend`, { method: 'POST' }),
    onSuccess: async () => {
      setSent(true)
      await refresh()
    },
  })
  const cancel = useMutation({
    mutationFn: () => api(`/families/${familyId}/invites/${invite.id}`, { method: 'DELETE' }),
    onSuccess: refresh,
  })
  const [now] = useState(Date.now)
  const state = inviteState(invite, now)
  // A link invite is named for who it's for; its email, if anyone has typed one in, isn't shown.
  const who = invite.email ?? invite.name ?? invite.person ?? 'Someone'
  const Icon = invite.via === 'link' ? Link2 : Mail
  const said = invite.declined
    ? 'Said no thanks'
    : [invite.via === 'link' && 'Link', invite.person && `Invited as ${invite.person}`, state.said]
        .filter(Boolean)
        .join(' · ')
  return (
    <li className="flex flex-col gap-1 border-t px-4 py-3 first:border-t-0 nav:first:border-t">
      <div className="flex items-center gap-3">
        <span className="grid size-9 shrink-0 place-items-center rounded-full border border-dashed text-muted-foreground">
          <Icon className="size-4" aria-hidden />
        </span>
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate font-medium">{who}</span>
          <span className="text-sub text-muted-foreground">{said}</span>
        </span>
        <div className="flex shrink-0 gap-1">
          {canInvite && invite.via === 'email' && (
            <Button
              size="sm"
              variant={state.expired ? 'default' : 'outline'}
              disabled={resend.isPending}
              onClick={() => resend.mutate()}
              aria-label={`Send the invite to ${who} again`}
            >
              Resend
            </Button>
          )}
          <Button
            size="sm"
            variant="ghost"
            disabled={cancel.isPending}
            onClick={() => cancel.mutate()}
            aria-label={`${invite.declined ? 'Clear' : 'Cancel'} the invite to ${who}`}
          >
            {invite.declined ? 'Clear' : 'Cancel'}
          </Button>
        </div>
      </div>
      {sent && !resend.isPending && (
        <p role="status" className="pl-12 text-sub text-muted-foreground">
          Sent again. The new link works for 7 days.
        </p>
      )}
      <FormError error={resend.error ?? cancel.error} />
    </li>
  )
}
